/**
 * Theme hook — 3-state: 'system' | 'light' | 'dark'.
 *
 * - 'system' (default per i nuovi utenti) → segue `prefers-color-scheme`
 *   in tempo reale (reagisce a cambi di OS/browser).
 * - 'light' / 'dark' → override esplicito persistito su localStorage.
 *
 * Applica `html.dark` per attivare le override CSS in `index.css`.
 */
import { useEffect, useState } from 'react';

const KEY = 'qp.theme';
// Valori ammessi nel localStorage. Tutto il resto = 'system'.
const VALID = new Set(['system', 'light', 'dark']);

function systemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStored() {
  try {
    const v = localStorage.getItem(KEY);
    return VALID.has(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

function effective(themeMode) {
  if (themeMode === 'dark') return 'dark';
  if (themeMode === 'light') return 'light';
  return systemPrefersDark() ? 'dark' : 'light';
}

function applyEffective(eff) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', eff === 'dark');
}

// Applica subito al primo import (evita FOUC)
if (typeof document !== 'undefined') {
  applyEffective(effective(readStored()));
}

export function useTheme() {
  const [theme, setThemeState] = useState(readStored); // 'system' | 'light' | 'dark'
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Listener su cambi system color-scheme — utile solo quando theme==='system'
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemDark(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  // Persistenza + applicazione classe.
  // Deps intenzionali [theme, systemDark]: KEY/applyEffective/eff sono
  // costanti module-level o calcoli locali, non variano fra render.
  useEffect(() => {
    const eff = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;
    applyEffective(eff);
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      /* quota piena */
    }
  }, [theme, systemDark]);

  const isDark = theme === 'dark' || (theme === 'system' && systemDark);

  // Ciclo a 3 stati: system → light → dark → system
  const cycle = () =>
    setThemeState((t) => (t === 'system' ? 'light' : t === 'light' ? 'dark' : 'system'));

  return {
    theme,
    setTheme: setThemeState,
    cycle,
    isDark,
    isSystem: theme === 'system',
    effective: isDark ? 'dark' : 'light',
  };
}
